import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  CalendarClock,
  Columns3,
  CreditCard,
  Euro,
  FileText,
  Globe,
  Handshake,
  Headset,
  Home,
  KeyRound,
  Loader2,
  Map as MapIcon,
  Network,
  Plus,
  Printer,
  Scale,
  ScrollText,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { adminAPI } from '../../services/adminApi';
import { confirmDiscard, hasUnsavedChanges } from '../../utils/unsavedChanges';
import { SheetField, SheetSection } from '../settings/sheet';
import { DANGER_GHOST_BTN, DetailPanel, GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN, StatusChip } from '../shared/listPage';
import SuperAdminShell, { ShellGroup } from './SuperAdminShell';
import SuperAdminDashboardTab from './SuperAdminDashboardTab';
import SuperAdminStoresTab, { StorePrefill } from './SuperAdminStoresTab';
import SuperAdminOrgsTab from './SuperAdminOrgsTab';
import SuperAdminAuditLogsTab from './SuperAdminAuditLogsTab';
import SuperAdminFiscalTab from './SuperAdminFiscalTab';
import SuperAdminPlatformsTab from './SuperAdminPlatformsTab';
import SuperAdminReconTab from './SuperAdminReconTab';
import SuperAdminBillingTab from './SuperAdminBillingTab';
import SuperAdminPosProviders from './SuperAdminPosProviders';
import SuperAdminOrgTools from './SuperAdminOrgTools';
import SalesWorkspace from '../sales/SalesWorkspace';
import { Lead, storePrefillFromLead } from '../sales/salesModel';
import { salesAPI } from '../../services/salesApi';

/**
 * Super admin (νέα εμφάνιση, 14/10/2026): πλαϊνό μενού σε ομάδες (SuperAdminShell, το μοτίβο της
 * διαχείρισης καταστήματος) αντί για οριζόντιες καρτέλες. Ίδιες σελίδες, ίδια ids και ίδια σειρά με πριν·
 * οι ομάδες απλώς τις ταξινομούν. Τα «Χρήστες support» και «Κλειδιά πρόσβασης» (ως τότε εικονίδια στην
 * κεφαλίδα) είναι πλέον στοιχεία της ομάδας «Σύστημα» και ανοίγουν συρτάρι — ίδια endpoints, ίδιοι έλεγχοι.
 */

// «Υπάλληλοι» και «Κατάλογος» αφαιρέθηκαν σκόπιμα από το superadmin view —
// η διαχείρισή τους γίνεται μέσα από κάθε κατάστημα / οργανισμό.
// «Εκδόσεις agent» και «Κατάλογος πλατφορμών» (15/10/2026): έφυγαν από τα κεντρικά των οργανισμών, είναι εδώ.
// «Πωλήσεις» (migrations 285–287, 3/11/2026): «Σήμερα», «Υποψήφιοι πελάτες», «Στάδια», «Χάρτης», «Αναφορές» — οι
// ίδιες σελίδες με τον ρόλο 'sales'.
type AdminTab = 'dashboard' | 'stores' | 'organizations' | 'sales-today' | 'sales-leads' | 'sales-board' | 'sales-map'
  | 'sales-reports' | 'billing' | 'fiscal' | 'recon' | 'platforms' | 'pos-providers' | 'platform-catalog' | 'agent-releases'
  | 'audit-logs';

const TABS: readonly AdminTab[] = ['dashboard', 'stores', 'organizations', 'sales-today', 'sales-leads', 'sales-board',
  'sales-map', 'sales-reports', 'billing', 'fiscal', 'recon', 'platforms', 'pos-providers', 'platform-catalog', 'agent-releases',
  'audit-logs'];
const isAdminTab = (id: string): id is AdminTab => (TABS as readonly string[]).includes(id);

const SuperAdminPanel: React.FC = () => {
  const { t } = useTranslation(['superAdminPanel', 'admin', 'settings', 'common']);
  const { logout, employee } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');

  // Χρήστες support / πωλήσεων (ρόλοι 'support' / 'sales' στο HQ κατάστημα)
  const [showSupportUsers, setShowSupportUsers] = useState(false);
  const [showSalesUsers, setShowSalesUsers] = useState(false);

  // Πωλήσεις → «Έγινε πελάτης»: «Νέο κατάστημα» συμπληρωμένο από τον υποψήφιο, και μετά σύνδεση των δύο.
  const [storePrefill, setStorePrefill] = useState<StorePrefill | null>(null);
  const createStoreFromLead = (lead: Lead) => {
    setStorePrefill(storePrefillFromLead(lead, async (storeId, storeName) => {
      try {
        await salesAPI.addActivity(lead.id, { type: 'note', body: `Έγινε κατάστημα: ${storeName} (#${storeId})` },
          { status: 'won', won_store_id: storeId });
        return { ok: true, message: `Ο υποψήφιος «${lead.name}» έγινε πελάτης και συνδέθηκε με το κατάστημα.` };
      } catch {
        return { ok: false, message: `Το κατάστημα δημιουργήθηκε· η σύνδεση με τον υποψήφιο απέτυχε — διάλεξέ το από την κάρτα του («Πελάτης»).` };
      }
    }));
    setActiveTab('stores');
  };

  // Master access (πασπαρτού password/PIN)
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [masterStatus, setMasterStatus] = useState<{ password_set: boolean; pin_set: boolean } | null>(null);
  const [masterForm, setMasterForm] = useState({ password: '', pin: '' });
  const [masterSaving, setMasterSaving] = useState(false);

  useEffect(() => {
    if (showMasterModal) {
      adminAPI.getMasterAccess().then((d: any) => setMasterStatus(d)).catch(() => setMasterStatus(null));
    }
  }, [showMasterModal]);

  const handleSaveMaster = async () => {
    if (!masterForm.password && !masterForm.pin) return;
    if (masterForm.password && masterForm.password.length < 12) {
      alert(t('superAdminPanel:master.passwordTooShort'));
      return;
    }
    if (masterForm.pin && !/^\d{6}$/.test(masterForm.pin)) {
      alert(t('superAdminPanel:master.pinInvalid'));
      return;
    }
    try {
      setMasterSaving(true);
      const d: any = await adminAPI.setMasterAccess(masterForm.password || undefined, masterForm.pin || undefined);
      setMasterStatus({ password_set: d.password_set, pin_set: d.pin_set });
      setMasterForm({ password: '', pin: '' });
      alert(t('superAdminPanel:master.saved'));
    } catch (e: any) {
      alert(e.response?.data?.error || t('superAdminPanel:master.saveFailed'));
    } finally {
      setMasterSaving(false);
    }
  };

  const closeMaster = () => {
    setShowMasterModal(false);
    setMasterForm({ password: '', pin: '' });
  };

  // Όπως η διαχείριση καταστήματος: μια σελίδα με αλλαγές που δεν αποθηκεύτηκαν (hooks/useDirtyForm με
  // guardId) ρωτά πριν χαθούν· χωρίς τέτοια σελίδα δεν αλλάζει τίποτα.
  const handleLogout = async () => {
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    await logout();
    window.location.href = '/login';
  };

  const selectTab = (id: string) => {
    if (!isAdminTab(id) || id === activeTab) return;
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    setActiveTab(id);
  };

  const groups: ShellGroup[] = [
    {
      id: 'overview',
      label: t('superAdminPanel:nav.groups.overview'),
      items: [{ id: 'dashboard', label: t('superAdminPanel:nav.dashboard'), icon: Home }],
    },
    {
      id: 'customers',
      label: t('superAdminPanel:nav.groups.customers'),
      items: [
        { id: 'stores', label: t('admin:storesTab'), icon: Building2 },
        { id: 'organizations', label: t('admin:orgsTab'), icon: Network },
      ],
    },
    {
      id: 'sales',
      label: t('superAdminPanel:nav.groups.sales'),
      items: [
        { id: 'sales-today', label: t('superAdminPanel:nav.salesToday'), icon: CalendarClock },
        { id: 'sales-leads', label: t('superAdminPanel:nav.salesLeads'), icon: Handshake },
        { id: 'sales-board', label: t('superAdminPanel:nav.salesBoard'), icon: Columns3 },
        { id: 'sales-map', label: t('superAdminPanel:nav.salesMap'), icon: MapIcon },
        { id: 'sales-reports', label: t('superAdminPanel:nav.salesReports'), icon: BarChart3 },
      ],
    },
    {
      id: 'finance',
      label: t('superAdminPanel:nav.groups.finance'),
      items: [
        { id: 'billing', label: t('superAdminPanel:nav.billing'), icon: Euro },
        { id: 'fiscal', label: t('admin:fiscalTab'), icon: FileText },
        { id: 'recon', label: t('superAdminPanel:nav.recon'), icon: Scale },
      ],
    },
    {
      id: 'connections',
      label: t('superAdminPanel:nav.groups.connections'),
      items: [
        { id: 'platforms', label: t('superAdminPanel:nav.platforms'), icon: Globe },
        // Στοιχεία σύνδεσης παρόχων POS για όλα τα καταστήματα (migration 269)
        { id: 'pos-providers', label: t('settings:pos.platform.tab'), icon: CreditCard },
        { id: 'platform-catalog', label: t('superAdminPanel:nav.platformCatalog'), icon: BookOpen },
      ],
    },
    {
      id: 'system',
      label: t('superAdminPanel:nav.groups.system'),
      items: [
        { id: 'agent-releases', label: t('superAdminPanel:nav.agentReleases'), icon: Printer },
        { id: 'audit-logs', label: t('admin:auditTab'), icon: ScrollText },
        { id: 'support-users', label: t('superAdminPanel:nav.supportUsers'), icon: Headset, action: () => setShowSupportUsers(true) },
        { id: 'sales-users', label: t('superAdminPanel:nav.salesUsers'), icon: Briefcase, action: () => setShowSalesUsers(true) },
        { id: 'master-access', label: t('superAdminPanel:nav.masterAccess'), icon: KeyRound, action: () => setShowMasterModal(true) },
      ],
    },
  ];

  const userName = [employee?.first_name, employee?.last_name].filter(Boolean).join(' ') || employee?.name || '';
  const masterHint = (set: boolean | undefined) => (set ? t('superAdminPanel:master.isSet') : undefined);

  return (
    <SuperAdminShell
      groups={groups}
      activeId={activeTab}
      onSelect={selectTab}
      userName={userName}
      roleLabel={t('superAdminPanel:header.role')}
      onLogout={handleLogout}
    >
      {activeTab === 'dashboard' && <SuperAdminDashboardTab />}
      {activeTab === 'stores' && <SuperAdminStoresTab prefill={storePrefill} />}
      {activeTab === 'organizations' && <SuperAdminOrgsTab />}
      {activeTab === 'sales-today' && <SalesWorkspace view="today" onShowList={() => selectTab('sales-leads')} onCreateStore={createStoreFromLead} />}
      {activeTab === 'sales-leads' && <SalesWorkspace view="list" onCreateStore={createStoreFromLead} />}
      {activeTab === 'sales-board' && <SalesWorkspace view="board" onCreateStore={createStoreFromLead} />}
      {activeTab === 'sales-map' && <SalesWorkspace view="map" onCreateStore={createStoreFromLead} />}
      {activeTab === 'sales-reports' && <SalesWorkspace view="reports" onCreateStore={createStoreFromLead} />}
      {activeTab === 'billing' && <SuperAdminBillingTab />}
      {activeTab === 'audit-logs' && <SuperAdminAuditLogsTab />}
      {activeTab === 'fiscal' && <SuperAdminFiscalTab />}
      {activeTab === 'recon' && <SuperAdminReconTab />}
      {activeTab === 'platforms' && <SuperAdminPlatformsTab />}
      {activeTab === 'pos-providers' && <SuperAdminPosProviders />}
      {activeTab === 'platform-catalog' && <SuperAdminOrgTools tool="platform-catalog" />}
      {activeTab === 'agent-releases' && <SuperAdminOrgTools tool="agent-releases" />}

      {/* Χρήστες support: φορτώνουν κάθε φορά που ανοίγει το συρτάρι */}
      {showSupportUsers && (
        <SupportUsersPanel hqStoreId={(employee as any)?.store_id || 1} onClose={() => setShowSupportUsers(false)} />
      )}
      {showSalesUsers && (
        <SupportUsersPanel roleName="sales" hqStoreId={(employee as any)?.store_id || 1} onClose={() => setShowSalesUsers(false)} />
      )}

      {/* Κλειδιά πρόσβασης (master password / PIN) */}
      <DetailPanel
        open={showMasterModal}
        onClose={closeMaster}
        title={t('superAdminPanel:master.title')}
        subtitle={t('superAdminPanel:master.subtitle')}
        testId="sa-master-panel"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeMaster}>{t('common:cancel')}</button>
            <button
              type="submit"
              form="sa-master-form"
              className={PRIMARY_BTN}
              disabled={masterSaving || (!masterForm.password && !masterForm.pin)}
            >
              {masterSaving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {t('superAdminPanel:master.save')}
            </button>
          </>
        )}
      >
        <form id="sa-master-form" className="space-y-5" onSubmit={e => { e.preventDefault(); handleSaveMaster(); }}>
          <p className="text-sm text-t3">{t('superAdminPanel:master.intro')}</p>
          <SheetField label={t('superAdminPanel:master.password')} htmlFor="sa-master-password" hint={masterHint(masterStatus?.password_set)}>
            <input
              id="sa-master-password"
              type="password"
              value={masterForm.password}
              onChange={(e) => setMasterForm({ ...masterForm, password: e.target.value })}
              placeholder={t('superAdminPanel:master.passwordPlaceholder')}
              autoComplete="one-time-code"
              aria-describedby={masterStatus?.password_set ? 'sa-master-password-hint' : undefined}
              className={INPUT_CLS}
            />
          </SheetField>
          <SheetField label={t('superAdminPanel:master.pin')} htmlFor="sa-master-pin" hint={masterHint(masterStatus?.pin_set)}>
            <input
              id="sa-master-pin"
              type="password"
              inputMode="numeric"
              value={masterForm.pin}
              maxLength={6}
              onChange={(e) => setMasterForm({ ...masterForm, pin: e.target.value.replace(/\D/g, '') })}
              placeholder={t('superAdminPanel:master.pinPlaceholder')}
              autoComplete="one-time-code"
              aria-describedby={masterStatus?.pin_set ? 'sa-master-pin-hint' : undefined}
              className={`${INPUT_CLS} max-w-[12rem]`}
            />
          </SheetField>
        </form>
      </DetailPanel>
    </SuperAdminShell>
  );
};

// ── Χρήστες support ──────────────────────────────────────────────────────────
// Οι χρήστες support είναι employees του HQ καταστήματος με ρόλο 'support'
// (migration 207). Μπαίνουν από το ίδιο login (κατάστημα HQ + δικό τους PIN)
// και βλέπουν το SupportPanel. Κάθε ενέργειά τους καταγράφεται ονομαστικά.
// Ίδιο συρτάρι και για τους χρήστες πωλήσεων (ρόλος 'sales', migration 285): αλλάζουν μόνο τα κείμενα.
const SupportUsersPanel: React.FC<{ hqStoreId: number; onClose: () => void; roleName?: 'support' | 'sales' }> = ({
  hqStoreId, onClose, roleName = 'support',
}) => {
  const { t } = useTranslation(['superAdminPanel', 'listPage', 'common']);
  const isSales = roleName === 'sales';
  const txt = isSales ? {
    title: t('superAdminPanel:salesUsers.title'),
    subtitle: t('superAdminPanel:salesUsers.subtitle'),
    intro: t('superAdminPanel:salesUsers.intro'),
    noRole: t('superAdminPanel:salesUsers.noRole'),
    empty: t('superAdminPanel:salesUsers.empty'),
    newTitle: t('superAdminPanel:salesUsers.newTitle'),
  } : {
    title: t('superAdminPanel:support.title'),
    subtitle: t('superAdminPanel:support.subtitle'),
    intro: t('superAdminPanel:support.intro'),
    noRole: t('superAdminPanel:support.noRole'),
    empty: t('superAdminPanel:support.empty'),
    newTitle: t('superAdminPanel:support.newTitle'),
  };
  const testPrefix = isSales ? 'sa-sales-users' : 'sa-support';
  const [users, setUsers] = useState<any[]>([]);
  const [supportRoleId, setSupportRoleId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', pin_code: '' });

  const load = async () => {
    try {
      setLoading(true);
      const [roles, employees]: any[] = await Promise.all([
        adminAPI.getRoles(),
        adminAPI.getEmployees(),
      ]);
      const supportRole = (roles || []).find((r: any) => r.name === roleName);
      setSupportRoleId(supportRole?.id ?? null);
      setUsers(supportRole ? (employees || []).filter((e: any) => e.role_id === supportRole.id) : []);
    } catch (e) {
      console.error('Support users load failed:', e);
    } finally {
      setLoading(false);
    }
  };
  // Μία φορά, όταν ανοίγει το συρτάρι (όπως το παλιό παράθυρο).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const fullName = (u: any) => `${u.first_name} ${u.last_name}`;

  const createUser = async () => {
    if (!supportRoleId) return;
    if (!form.first_name.trim() || !form.email.trim()) { alert(t('superAdminPanel:support.nameEmailRequired')); return; }
    if (!/^\d{4,6}$/.test(form.pin_code)) { alert(t('superAdminPanel:support.pinInvalid')); return; }
    try {
      setBusy(true);
      await adminAPI.createEmployee({
        store_id: hqStoreId,
        role_id: supportRoleId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || '-',
        email: form.email.trim(),
        pin_code: form.pin_code,
      });
      setForm({ first_name: '', last_name: '', email: '', pin_code: '' });
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || t('superAdminPanel:support.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async (u: any) => {
    const pin = window.prompt(t('superAdminPanel:support.pinPrompt', { name: fullName(u) }));
    if (pin == null) return;
    if (!/^\d{4,6}$/.test(pin)) { alert(t('superAdminPanel:support.pinInvalid')); return; }
    try {
      setBusy(true);
      await adminAPI.resetEmployeePin(u.id, pin);
      alert(t('superAdminPanel:support.pinChanged'));
    } catch (e: any) {
      alert(e.response?.data?.error || t('superAdminPanel:support.pinFailed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u: any) => {
    const msg = u.is_active
      ? t('superAdminPanel:support.confirmDeactivate', { name: fullName(u) })
      : t('superAdminPanel:support.confirmActivate', { name: fullName(u) });
    if (!window.confirm(msg)) return;
    try {
      setBusy(true);
      if (u.is_active) await adminAPI.deleteEmployee(u.id);
      else await adminAPI.updateEmployee(u.id, { is_active: true });
      await load();
    } catch (e: any) {
      alert(e.response?.data?.error || t('superAdminPanel:support.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailPanel
      open
      onClose={onClose}
      title={txt.title}
      subtitle={txt.subtitle}
      width="lg"
      testId={`${testPrefix}-panel`}
      footer={<button type="button" className={GHOST_BTN} onClick={onClose}>{t('listPage:close')}</button>}
    >
      <p className="text-sm text-t3 pb-5">{txt.intro}</p>

      {loading ? (
        <div role="status" className="flex items-center gap-2 border-t border-line py-10 justify-center text-sm text-t3">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          {t('listPage:loading')}
        </div>
      ) : supportRoleId === null ? (
        <Notice testId={`${testPrefix}-no-role`}>{txt.noRole}</Notice>
      ) : (
        <>
          <SheetSection id="sa-support-users" title={t('superAdminPanel:support.usersTitle')}>
            {users.length === 0 ? (
              <p className="text-sm text-t3">{txt.empty}</p>
            ) : (
              <ul className="divide-y divide-line" data-testid={isSales ? 'sa-sales-users-list' : 'sa-support-users'}>
                {users.map(u => (
                  <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <p className={`flex flex-wrap items-center gap-2 text-sm font-medium ${u.is_active ? 'text-t1' : 'text-t3'}`}>
                        <span>{fullName(u)}</span>
                        {!u.is_active && <StatusChip tone="muted">{t('superAdminPanel:support.inactive')}</StatusChip>}
                      </p>
                      <p className="text-xs text-t3 truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" className={GHOST_BTN} onClick={() => resetPin(u)} disabled={busy}>
                        {t('superAdminPanel:support.newPin')}
                      </button>
                      <button
                        type="button"
                        className={u.is_active ? DANGER_GHOST_BTN : GHOST_BTN}
                        onClick={() => toggleActive(u)}
                        disabled={busy}
                      >
                        {u.is_active ? t('superAdminPanel:support.deactivate') : t('superAdminPanel:support.activate')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SheetSection>

          {/* Νέος χρήστης */}
          <SheetSection id="sa-support-new" title={txt.newTitle} hint={t('superAdminPanel:support.newHint')}>
            <form onSubmit={e => { e.preventDefault(); createUser(); }}>
              <div className="xs-grid2">
                <SheetField label={t('superAdminPanel:support.firstName')} htmlFor="sa-support-first">
                  <input id="sa-support-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={INPUT_CLS} autoComplete="one-time-code" />
                </SheetField>
                <SheetField label={t('superAdminPanel:support.lastName')} htmlFor="sa-support-last">
                  <input id="sa-support-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={INPUT_CLS} autoComplete="one-time-code" />
                </SheetField>
                <SheetField label={t('superAdminPanel:support.email')} htmlFor="sa-support-email">
                  <input id="sa-support-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT_CLS} autoComplete="one-time-code" />
                </SheetField>
                <SheetField label={t('superAdminPanel:support.pin')} htmlFor="sa-support-pin">
                  <input
                    id="sa-support-pin"
                    inputMode="numeric"
                    value={form.pin_code}
                    maxLength={6}
                    onChange={(e) => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, '') })}
                    className={INPUT_CLS}
                    autoComplete="one-time-code"
                  />
                </SheetField>
              </div>
              <div className="mt-5 flex justify-end">
                <button type="submit" className={PRIMARY_BTN} disabled={busy}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Plus className="w-4 h-4" aria-hidden="true" />}
                  {t('superAdminPanel:support.create')}
                </button>
              </div>
            </form>
          </SheetSection>
        </>
      )}
    </DetailPanel>
  );
};

export default SuperAdminPanel;
